/**
 * Toki Mat Designer — v0.10 (фабрика-ready: TIFF CMYK FOGRA39, K100 vector mask, soft-proof, ТЗ-модалка)
 * 3-колоночный интерфейс: Слева (инструменты + слои) / Центр (превью) / Справа (продукт/объект).
 */
import * as fabric from 'fabric';
import './style.css';

const INCH_MM = 25.4;
// Пороги DPI по умолчанию (переопределяются cfg.dpi_min / cfg.dpi_recommend из шорткода).
// MIN_DPI_WARNING — ниже этого значения save()/addToCart блокируется.
// RECOMMENDED_DPI — ниже показываем варнинг, но разрешаем.
const MIN_DPI_WARNING = 200;
const RECOMMENDED_DPI = 300;

// Soft-proof: грубая CSS-симуляция CMYK (saturate понижен, contrast слегка снижен).
// Это не точный ICC-просмотр, но даёт ощущение "не все RGB цвета попадут в CMYK".
const SOFT_PROOF_FILTER = 'saturate(0.86) contrast(0.96) brightness(0.97)';
const THICKNESS_OPTIONS = [
  { value: 2, label: '2 мм' },
  { value: 3, label: '3 мм' },
  { value: 4, label: '4 мм' },
  { value: 5, label: '5 мм' },
];
const STITCH_COLORS = [
  { value: '#111111', label: 'Чёрный' },
  { value: '#ffffff', label: 'Белый' },
  { value: '#8b1c2a', label: 'Бордо' },
  { value: '#d92626', label: 'Алый' },
  { value: '#0d5c2e', label: 'Зелёный' },
  { value: '#9ad9b9', label: 'Мятный' },
  { value: '#1a47a3', label: 'Синий' },
  { value: '#5b2aa0', label: 'Фиолетовый' },
  { value: '#8a8a8a', label: 'Серый' },
  { value: '#c8a15a', label: 'Золотистый' },
];
const MATERIAL_OPTIONS = [
  { value: 'fabric-water', label: 'Ткань с водоотталк.', active: true },
];

class MatDesigner {
  constructor(root) {
    this.root = root;
    this.cfg = JSON.parse(root.dataset.tmdConfig);

    this.printDpi = this.cfg.print_dpi || 300;
    this.widthMm = this.cfg.width_mm || 800;
    this.heightMm = this.cfg.height_mm || 300;
    this.bleedMm = this.cfg.bleed_mm || 7.5;
    this.safeMm = this.cfg.safe_mm || 5;
    // DPI-пороги (из шорткода или дефолты)
    this.dpiMin = Number(this.cfg.dpi_min) || MIN_DPI_WARNING;
    this.dpiRecommend = Number(this.cfg.dpi_recommend) || RECOMMENDED_DPI;
    // Approval: клиент должен подтвердить макет перед заказом
    this.designApproved = false;
    this.MAX_DISPLAY_DESKTOP = 540;  // десктопный потолок display-канваса по большей стороне
    this.MIN_DISPLAY = 240;          // минимум, чтобы UI оставался юзабельным

    // Шаблоны (все опубликованные размеры из WP CPT)
    this.templates = Array.isArray(this.cfg.templates) ? this.cfg.templates : [];
    this.currentTemplateId = this.cfg.template_id || (this.templates[0]?.template_id ?? 0);
    this.currentProductId = this.cfg.product_id || 0;
    this.currencySymbol = this.cfg.currency_symbol || '₽';
    this.freeShippingMin = this.cfg.free_shipping_min || 0;

    // Опции товара (default)
    this.thicknessMm = this.cfg.thickness_mm || 3;
    this.material = this.cfg.material || 'fabric-water';
    this.bgColor = this.cfg.bg_color || '#ffffff';
    this.stitchColor = this.cfg.stitch_color || '#111111';
    this.softProofOn = false;
    this.lockAspectRatio = true; // по умолчанию пропорции заблокированы

    // Масштабирование display: динамически под ширину viewport
    this.displayPxPerMm = this.computeDisplayPxPerMm();
    this.displayWidth = Math.round(this.widthMm * this.displayPxPerMm);
    this.displayHeight = Math.round(this.heightMm * this.displayPxPerMm);

    this.printWidthPx = Math.round((this.widthMm / INCH_MM) * this.printDpi);
    this.printHeightPx = Math.round((this.heightMm / INCH_MM) * this.printDpi);
    this.printMultiplier = this.printWidthPx / this.displayWidth;

    this.history = [];
    this.historyIndex = -1;
    this.isRestoring = false;
    this.savedIds = null;  // preview_id + print_id после последнего save()

    this.renderLayout();
    this.initCanvas();
    this.bindControls();
    // apply initial product-option visuals
    this.setBgColor(this.bgColor);
    this.setStitchColor(this.stitchColor);
    this.refreshLayers();
    this.renderObjectPanel(null);
    // Попытка восстановить состояние из localStorage (после всего init)
    this.loadAutostate();
    this.setupResponsiveCanvas();
    // Начальное состояние CTA: заблокирован (нет approval, возможно нет картинок)
    this.updateSaveButton();
  }

  // Находит шаблон по текущим widthMm×heightMm (для связи размер → template_id → product)
  findTemplateBySize(w, h) {
    return this.templates.find(t => Math.round(t.width_mm) === Math.round(w) && Math.round(t.height_mm) === Math.round(h));
  }

  currentTemplate() {
    return this.templates.find(t => t.template_id === this.currentTemplateId) || this.templates[0] || null;
  }

  currentPrice() {
    const t = this.currentTemplate();
    return t?.price || this.cfg.product?.price || 0;
  }

  currentPriceHtml() {
    const t = this.currentTemplate();
    if (t?.price_html) return t.price_html;
    if (t?.price) return `${t.price.toLocaleString('ru-RU')} ${this.currencySymbol}`;
    return this.cfg.product?.price_html || 'Цена по запросу';
  }

  // --- РАЗМЕТКА ---
  renderLayout() {
    const priceHtml = this.currentPriceHtml();
    const title = `Коврик ${this.widthMm}×${this.heightMm} мм`;
    // Строка доставки (бесплатная от N₽)
    const freeShipNote = this.freeShippingMin > 0
      ? `Бесплатная доставка от ${this.freeShippingMin.toLocaleString('ru-RU')} ${this.currencySymbol}`
      : '';
    this.root.innerHTML = `
      <div class="tmd-app">
        <header class="tmd-topbar">
          <div class="tmd-brand">
            <span class="tmd-brand-dot"></span>
            <div>
              <div class="tmd-brand-title">${this.escape(title)}</div>
              <div class="tmd-brand-sub">${this.widthMm}×${this.heightMm} мм · ${this.printDpi} DPI · печать ${this.printWidthPx}×${this.printHeightPx} px</div>
            </div>
          </div>
          <div class="tmd-topbar-actions">
            <button class="tmd-btn-ghost tmd-btn-soft-proof" data-action="toggle-soft-proof" title="Soft-proof: грубая CMYK-симуляция">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 3v18M3 12h18"/></svg>
              CMYK-превью
            </button>
            <button class="tmd-btn-ghost" data-action="show-specs" title="Требования к макету (ТЗ фабрики)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              Требования
            </button>
            <div class="tmd-cta-box">
              <div class="tmd-price">${priceHtml}</div>
              ${freeShipNote ? `<div class="tmd-free-ship">${this.escape(freeShipNote)}</div>` : ''}
              <label class="tmd-approval">
                <input type="checkbox" id="tmd-approval-check">
                <span>Я проверил(а) макет: размеры, текст, отступы. Согласен(на) на печать как есть.</span>
              </label>
              <div class="tmd-cta-block-note" id="tmd-cta-block-note"></div>
              <button class="tmd-cta" data-action="add-to-cart" disabled>
                <span>Добавить в корзину</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
            </div>
          </div>
        </header>

        <div class="tmd-workspace">

          <!-- Левая панель: инструменты + слои -->
          <aside class="tmd-panel tmd-left">
            <div class="tmd-tabs" data-side="left">
              <button class="tmd-tab active" data-tab="design">Дизайн</button>
              <button class="tmd-tab" data-tab="files">Файлы</button>
            </div>

            <div class="tmd-tab-body" data-content="design">
              <div class="tmd-group">
                <label class="tmd-upload-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <span>Загрузить изображение</span>
                  <input type="file" accept="image/*" id="tmd-upload" hidden>
                </label>
                <p class="tmd-hint" id="tmd-upload-hint">PNG / JPG. Рекомендуется ≥${RECOMMENDED_DPI} DPI на печать.</p>
              </div>

              <div class="tmd-group">
                <div class="tmd-group-title">Добавить</div>
                <div class="tmd-tool-grid">
                  <button class="tmd-tool" data-action="add-text" title="Текст">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
                    Текст
                  </button>
                  <button class="tmd-tool" data-action="add-rect" title="Прямоугольник">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                    Прямо&shy;угольник
                  </button>
                  <button class="tmd-tool" data-action="add-circle" title="Круг">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/></svg>
                    Круг
                  </button>
                  <button class="tmd-tool" data-action="add-line" title="Линия">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="19" x2="19" y2="5"/></svg>
                    Линия
                  </button>
                </div>
              </div>
            </div>

            <div class="tmd-tab-body" data-content="files" hidden>
              <p class="tmd-hint">Ваши недавно загруженные файлы появятся здесь.</p>
            </div>

            <div class="tmd-layers">
              <div class="tmd-group-title">Слои</div>
              <ul class="tmd-layers-list" id="tmd-layers-list">
                <li class="tmd-empty">Пока пусто</li>
              </ul>
            </div>
          </aside>

          <!-- Центр: превью и нижний тулбар -->
          <main class="tmd-center">
            <div class="tmd-canvas-frame" id="tmd-canvas-frame">
              <canvas id="tmd-canvas" width="${this.displayWidth}" height="${this.displayHeight}"></canvas>
              <div class="tmd-empty-state" id="tmd-empty-state">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                <div class="tmd-empty-title">Перетащите изображение сюда</div>
                <div class="tmd-empty-sub">или нажмите «Дизайн» внизу экрана</div>
                <label class="tmd-empty-upload-btn">
                  Выбрать файл
                  <input type="file" accept="image/*" hidden>
                </label>
              </div>
              <div class="tmd-drop-overlay" id="tmd-drop-overlay">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <span>Перетащите изображение сюда</span>
              </div>
            </div>
            <div class="tmd-rotate-hint" id="tmd-rotate-hint">
              <button class="tmd-rotate-btn" data-action="rotate-landscape">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>
                На весь экран
              </button>
            </div>
            <div class="tmd-bottom-toolbar">
              <button data-action="undo" title="Отменить (Ctrl+Z)">↶</button>
              <button data-action="redo" title="Вернуть (Ctrl+Y)">↷</button>
              <div class="tmd-divider"></div>
              <button data-action="duplicate" title="Дубликат (Ctrl+D)">⧉</button>
              <button data-action="delete" title="Удалить (Del)">🗑</button>
              <div class="tmd-divider"></div>
              <button data-action="back" title="На задний план">↓ Назад</button>
              <button data-action="front" title="На передний план">↑ Вперёд</button>
              <div class="tmd-divider"></div>
              <button class="tmd-btn-lock active" data-action="toggle-lock" title="Сохранять пропорции при масштабировании">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                <span class="tmd-lock-label">Пропорции</span>
              </button>
            </div>
            <div class="tmd-dimensions-bar" id="tmd-dimensions-bar"></div>
            <div class="tmd-save-status" id="tmd-save-status"></div>
          </main>

          <!-- Правая панель: Продукт / Объект -->
          <aside class="tmd-panel tmd-right">
            <div class="tmd-tabs" data-side="right">
              <button class="tmd-tab active" data-tab="product">Продукт</button>
              <button class="tmd-tab" data-tab="object">Объект</button>
            </div>

            <div class="tmd-tab-body" data-content="product">
              <!-- Размер (readonly — выбирается шаблоном товара) -->
              <div class="tmd-field">
                <div class="tmd-field-label">Модель</div>
                <div class="tmd-field-value">${this.escape(title)}</div>
              </div>
              <div class="tmd-field">
                <div class="tmd-field-label">Размер коврика</div>
                <div class="tmd-pills tmd-pills-wrap" data-group="size">
                  ${(this.templates.length ? this.templates : [{template_id: 0, width_mm: this.widthMm, height_mm: this.heightMm, thickness_mm: this.thicknessMm, price: 0, price_html: ''}]).map(t => {
                    const active = t.template_id === this.currentTemplateId;
                    const label = `${t.width_mm}×${t.height_mm} мм`;
                    const priceLabel = t.price ? `${t.price.toLocaleString('ru-RU')} ${this.currencySymbol}` : '';
                    return `<button class="tmd-pill tmd-pill-size ${active ? 'active' : ''}"
                              data-tpl="${t.template_id}" data-w="${t.width_mm}" data-h="${t.height_mm}" data-pid="${t.product_id || 0}" data-price="${t.price || 0}">
                              <span>${label}</span>${priceLabel ? `<small>${priceLabel}</small>` : ''}
                            </button>`;
                  }).join('')}
                </div>
                <p class="tmd-hint tmd-hint-inline">Печать ${this.printDpi} DPI · <span id="tmd-print-info">${this.printWidthPx}×${this.printHeightPx} px</span></p>
              </div>

              <!-- Толщина -->
              <div class="tmd-field">
                <div class="tmd-field-label">Толщина материала</div>
                <div class="tmd-pills" data-group="thickness">
                  ${THICKNESS_OPTIONS.map(o => `
                    <button class="tmd-pill ${o.value === this.thicknessMm ? 'active' : ''}" data-value="${o.value}">${o.label}</button>
                  `).join('')}
                </div>
              </div>

              <!-- Материал -->
              <div class="tmd-field">
                <div class="tmd-field-label">Материал</div>
                <div class="tmd-pills" data-group="material">
                  ${MATERIAL_OPTIONS.map(o => `
                    <button class="tmd-pill ${o.value === this.material ? 'active' : ''}" data-value="${o.value}" ${o.active ? '' : 'disabled'}>${o.label}</button>
                  `).join('')}
                </div>
                <p class="tmd-hint tmd-hint-inline">Другие материалы — скоро.</p>
              </div>

              <!-- Цвет фона коврика -->
              <div class="tmd-field">
                <div class="tmd-field-label">Цвет фона коврика</div>
                <div class="tmd-bg-row">
                  <input type="color" id="tmd-bg-color" value="${this.bgColor}">
                  <button class="tmd-btn-ghost" data-action="bg-reset">Белый</button>
                </div>
                <p class="tmd-hint tmd-hint-inline">Виден только где нет картинки/фона.</p>
              </div>

              <!-- Цвет нити обшивки -->
              <div class="tmd-field">
                <div class="tmd-field-label">Цвет нити обшивки</div>
                <div class="tmd-swatches" data-group="stitch">
                  ${STITCH_COLORS.map(c => `
                    <button class="tmd-swatch ${c.value.toLowerCase() === this.stitchColor.toLowerCase() ? 'active' : ''}"
                            data-value="${c.value}"
                            style="background: ${c.value};"
                            title="${c.label}"></button>
                  `).join('')}
                  <label class="tmd-swatch tmd-swatch-custom" title="Свой цвет">
                    <input type="color" id="tmd-stitch-custom" value="${this.stitchColor}">
                    <span>+</span>
                  </label>
                </div>
              </div>

              <div class="tmd-field">
                <div class="tmd-field-label">Цена</div>
                <div class="tmd-field-value tmd-price-large" id="tmd-price-dynamic">${priceHtml}</div>
                ${freeShipNote ? `<p class="tmd-hint tmd-hint-inline">${this.escape(freeShipNote)}</p>` : ''}
              </div>
            </div>

            <div class="tmd-tab-body" data-content="object" hidden>
              <div id="tmd-object-props">
                <p class="tmd-empty">Выберите объект на холсте, чтобы изменить его свойства.</p>
              </div>
            </div>
          </aside>

          <!-- Mobile FAB bar (visible only ≤900px via CSS) -->
          <div class="tmd-mobile-fab-bar" id="tmd-mobile-fab-bar">
            <button class="tmd-mobile-fab" data-sheet="left">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Дизайн
            </button>
            <button class="tmd-mobile-fab" data-sheet="right">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
              Продукт
            </button>
          </div>
          <!-- Bottom sheet backdrop -->
          <div class="tmd-sheet-backdrop" id="tmd-sheet-backdrop"></div>

        </div>
      </div>
    `;
  }

  // --- CANVAS ---
  initCanvas() {
    const el = this.root.querySelector('#tmd-canvas');
    this.canvas = new fabric.Canvas(el, {
      width: this.displayWidth,
      height: this.displayHeight,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
    });

    if (this.cfg.background) {
      fabric.FabricImage.fromURL(this.cfg.background, { crossOrigin: 'anonymous' })
        .then((img) => {
          img.scaleToWidth(this.displayWidth);
          this.canvas.backgroundImage = img;
          this.canvas.requestRenderAll();
        })
        .catch((e) => console.warn('[TMD] background load failed', e));
    }

    // Две рамки:
    //   - safeZone (линия реза): внутрь на bleedMm от краёв канваса
    //   - safeInner (safe-zone/безопасное поле): дополнительно внутрь на safeMm от линии реза
    const bleedPx = this.bleedMm * this.displayPxPerMm;
    const safePx = this.safeMm * this.displayPxPerMm;
    this.safeZone = new fabric.Rect({
      left: bleedPx,
      top: bleedPx,
      width: this.displayWidth - 2 * bleedPx,
      height: this.displayHeight - 2 * bleedPx,
      fill: 'transparent',
      stroke: '#e74c3c',
      strokeDashArray: [6, 6],
      strokeWidth: 1.5,
      selectable: false,
      evented: false,
      excludeFromExport: true,
      data: { tmdSystem: true, label: 'cut' },
    });
    this.safeInner = new fabric.Rect({
      left: bleedPx + safePx,
      top: bleedPx + safePx,
      width: this.displayWidth - 2 * (bleedPx + safePx),
      height: this.displayHeight - 2 * (bleedPx + safePx),
      fill: 'transparent',
      stroke: '#2ecc71',
      strokeDashArray: [4, 4],
      strokeWidth: 1,
      selectable: false,
      evented: false,
      excludeFromExport: true,
      data: { tmdSystem: true, label: 'safe' },
    });
    this.canvas.add(this.safeZone);
    this.canvas.add(this.safeInner);
    this.canvas.requestRenderAll();

    const saveState = () => {
      if (this.isRestoring) return;
      const json = this.canvas.toJSON(['data']);
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(json);
      if (this.history.length > 50) this.history.shift();
      this.historyIndex = this.history.length - 1;
      this.refreshLayers();
      this.scheduleAutosave();
      this.updateEmptyState();
      this.updateToolbarState();
      this.updateSaveButton();
    };
    this.canvas.on('object:added', saveState);
    this.canvas.on('object:modified', saveState);
    this.canvas.on('object:removed', saveState);
    this.canvas.on('selection:created', (e) => { const o = this.canvas.getActiveObject(); this.renderObjectPanel(o); this.updateDpiHint(); this.updateDimensionsDisplay(o); this.switchRightTab('object'); });
    this.canvas.on('selection:updated', (e) => { const o = this.canvas.getActiveObject(); this.renderObjectPanel(o); this.updateDpiHint(); this.updateDimensionsDisplay(o); this.switchRightTab('object'); });
    this.canvas.on('selection:cleared', () => { this.renderObjectPanel(null); this.updateDpiHint(); this.updateDimensionsDisplay(null); this.switchRightTab('product'); });
    saveState();

    this.setupCanvasGestures();

    // Lock aspect ratio: enforce uniform scaling
    this.canvas.on('object:scaling', (e) => {
      const obj = e.target;
      if (!obj || (obj.data && obj.data.tmdSystem)) return;
      if (this.lockAspectRatio) {
        // Определяем какая ось изменилась больше — её значение применяем к обеим
        const sx = Math.abs(obj.scaleX || 1);
        const sy = Math.abs(obj.scaleY || 1);
        const prevSx = obj._tmdPrevSx || sx;
        const prevSy = obj._tmdPrevSy || sy;
        const dx = Math.abs(sx - prevSx);
        const dy = Math.abs(sy - prevSy);
        const dominant = dx >= dy ? sx : sy;
        obj.set({ scaleX: dominant * Math.sign(obj.scaleX || 1), scaleY: dominant * Math.sign(obj.scaleY || 1) });
      }
      obj._tmdPrevSx = Math.abs(obj.scaleX || 1);
      obj._tmdPrevSy = Math.abs(obj.scaleY || 1);
      // Обновляем панель размеров
      this.updateDimensionsDisplay(obj);
    });
    // Обновляем размеры при перемещении/повороте тоже
    this.canvas.on('object:moving', (e) => this.updateDimensionsDisplay(e.target));
    this.canvas.on('object:rotating', (e) => this.updateDimensionsDisplay(e.target));
    this.canvas.on('object:modified', (e) => {
      if (e.target) {
        e.target._tmdPrevSx = undefined;
        e.target._tmdPrevSy = undefined;
      }
    });

    console.log('[TMD v0.3] ready', {
      display: [this.displayWidth, this.displayHeight],
      print: [this.printWidthPx, this.printHeightPx],
      multiplier: this.printMultiplier,
    });
  }

  // --- CONTROLS ---
  bindControls() {
    this.root.querySelector('#tmd-upload').addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (!file) return;
      this.handleUpload(file);
      e.target.value = '';
    });

    this.root.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', () => this.handleAction(btn.dataset.action));
    });

    // Approval checkbox: разблокирует CTA «Добавить в корзину» (вкупе с DPI≥dpiMin).
    // META_APPROVAL=1 уйдёт в WC line item — Phase 2d мапит это на стадию воронки B24.
    const approvalCheck = this.root.querySelector('#tmd-approval-check');
    if (approvalCheck) {
      approvalCheck.addEventListener('change', (e) => {
        this.designApproved = !!e.target.checked;
        this.updateSaveButton();
      });
    }

    // Tabs
    this.root.querySelectorAll('.tmd-tabs').forEach((tabsEl) => {
      tabsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('.tmd-tab');
        if (!btn) return;
        const side = tabsEl.dataset.side;
        tabsEl.querySelectorAll('.tmd-tab').forEach(b => b.classList.toggle('active', b === btn));
        const targetTab = btn.dataset.tab;
        const panel = tabsEl.closest('.tmd-panel');
        panel.querySelectorAll('[data-content]').forEach(c => {
          c.hidden = c.dataset.content !== targetTab;
        });
      });
    });

    // Product options: size, thickness, material, stitch, bg
    const size = this.root.querySelector('[data-group="size"]');
    if (size) size.addEventListener('click', (e) => {
      const b = e.target.closest('.tmd-pill');
      if (!b || b.disabled) return;
      const tplId = Number(b.dataset.tpl) || 0;
      if (tplId) {
        this.selectTemplate(tplId);
      } else {
        this.setSize(Number(b.dataset.w), Number(b.dataset.h));
      }
    });
    const thickness = this.root.querySelector('[data-group="thickness"]');
    if (thickness) thickness.addEventListener('click', (e) => {
      const b = e.target.closest('.tmd-pill');
      if (!b || b.disabled) return;
      this.setThickness(Number(b.dataset.value));
    });
    const material = this.root.querySelector('[data-group="material"]');
    if (material) material.addEventListener('click', (e) => {
      const b = e.target.closest('.tmd-pill');
      if (!b || b.disabled) return;
      this.setMaterial(b.dataset.value);
    });
    const stitch = this.root.querySelector('[data-group="stitch"]');
    if (stitch) stitch.addEventListener('click', (e) => {
      const b = e.target.closest('.tmd-swatch:not(.tmd-swatch-custom)');
      if (!b) return;
      this.setStitchColor(b.dataset.value);
    });
    const stitchCustom = this.root.querySelector('#tmd-stitch-custom');
    if (stitchCustom) stitchCustom.addEventListener('input', (e) => {
      this.setStitchColor(e.target.value);
    });
    const bg = this.root.querySelector('#tmd-bg-color');
    if (bg) bg.addEventListener('input', (e) => {
      this.setBgColor(e.target.value);
    });

    // --- DRAG & DROP ---
    this.setupDragDrop();

    // --- MOBILE BOTTOM SHEET ---
    this.setupMobileSheet();

    // --- EMPTY STATE upload button ---
    const emptyUpload = this.root.querySelector('#tmd-empty-state input[type="file"]');
    if (emptyUpload) {
      emptyUpload.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) this.handleUpload(file);
        e.target.value = '';
      });
    }
    this.updateEmptyState();

    document.addEventListener('keydown', (e) => {
      const focused = document.activeElement;
      if (focused && ['INPUT', 'TEXTAREA'].includes(focused.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); this.undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) { e.preventDefault(); this.redo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key === 'd') { e.preventDefault(); this.duplicate(); }
      else if (e.key === 'Delete' || e.key === 'Backspace') { this.deleteSelected(); }
    });
  }

  // --- PRODUCT OPTIONS ---
  setSize(widthMm, heightMm) {
    if (widthMm === this.widthMm && heightMm === this.heightMm) return;

    // Старые размеры display — чтобы посчитать масштаб для объектов
    const oldDisplayWidth = this.displayWidth;
    const oldDisplayHeight = this.displayHeight;

    this.widthMm = widthMm;
    this.heightMm = heightMm;
    this.displayPxPerMm = this.computeDisplayPxPerMm(widthMm, heightMm);
    this.displayWidth = Math.round(widthMm * this.displayPxPerMm);
    this.displayHeight = Math.round(heightMm * this.displayPxPerMm);
    this.printWidthPx = Math.round((widthMm / INCH_MM) * this.printDpi);
    this.printHeightPx = Math.round((heightMm / INCH_MM) * this.printDpi);
    this.printMultiplier = this.printWidthPx / this.displayWidth;

    // Пересчёт объектов:
    //  - картинки (type image) переприменяют scale-to-cover к новому канвасу,
    //    сохраняя их исходные width/height без искажения
    //  - текст / фигуры: uniform scale = geom-среднее sx и sy, позиция пропорционально
    const sx = this.displayWidth / oldDisplayWidth;
    const sy = this.displayHeight / oldDisplayHeight;
    const uniform = Math.sqrt(sx * sy);
    this.isRestoring = true;
    this.canvas.getObjects().forEach((o) => {
      if (o.data && o.data.tmdSystem) return;
      const isImg = (o.type === 'image' || o.type === 'Image');
      if (isImg) {
        // переприменить cover, центрировать
        const cover = Math.max(
          this.displayWidth / o.width,
          this.displayHeight / o.height
        );
        o.set({
          scaleX: cover,
          scaleY: cover,
          left: (this.displayWidth - o.width * cover) / 2,
          top: (this.displayHeight - o.height * cover) / 2,
        });
      } else {
        o.set({
          left: (o.left || 0) * sx,
          top: (o.top || 0) * sy,
          scaleX: (o.scaleX || 1) * uniform,
          scaleY: (o.scaleY || 1) * uniform,
        });
      }
      o.setCoords();
    });
    this.isRestoring = false;

    this.savedIds = null; // дизайн изменился — предыдущий save инвалидируется
    this.canvas.setDimensions({ width: this.displayWidth, height: this.displayHeight });
    this.canvas.backgroundColor = this.bgColor;
    // Пересадить safe-zone с новыми размерами
    this.resetSafeZone();
    this.canvas.requestRenderAll();

    // Update UI pills
    this.root.querySelectorAll('[data-group="size"] .tmd-pill').forEach(b => {
      b.classList.toggle('active', Number(b.dataset.w) === widthMm && Number(b.dataset.h) === heightMm);
    });
    // Update brand-sub text in topbar
    const sub = this.root.querySelector('.tmd-brand-sub');
    if (sub) sub.textContent = `${widthMm}×${heightMm} мм · ${this.printDpi} DPI · печать ${this.printWidthPx}×${this.printHeightPx} px`;
    // Update brand-title
    const titleEl = this.root.querySelector('.tmd-brand-title');
    if (titleEl) titleEl.textContent = `Коврик ${widthMm}×${heightMm} мм`;
    // Print info hint
    const info = this.root.querySelector('#tmd-print-info');
    if (info) info.textContent = `${this.printWidthPx}×${this.printHeightPx} px`;

    this.refreshLayers();
    this.renderObjectPanel(null);
    this.updateDpiHint();
    this.scheduleAutosave();
  }

  resetSafeZone() {
    const bleedPx = this.bleedMm * this.displayPxPerMm;
    const safePx = this.safeMm * this.displayPxPerMm;
    if (this.safeZone) {
      this.safeZone.set({
        left: bleedPx,
        top: bleedPx,
        width: this.displayWidth - 2 * bleedPx,
        height: this.displayHeight - 2 * bleedPx,
      });
      if (!this.canvas.getObjects().includes(this.safeZone)) this.canvas.add(this.safeZone);
    }
    if (this.safeInner) {
      this.safeInner.set({
        left: bleedPx + safePx,
        top: bleedPx + safePx,
        width: this.displayWidth - 2 * (bleedPx + safePx),
        height: this.displayHeight - 2 * (bleedPx + safePx),
      });
      if (!this.canvas.getObjects().includes(this.safeInner)) this.canvas.add(this.safeInner);
    }
  }

  setThickness(mm) {
    this.thicknessMm = mm;
    this.root.querySelectorAll('[data-group="thickness"] .tmd-pill').forEach(b => {
      b.classList.toggle('active', Number(b.dataset.value) === mm);
    });
  }

  setMaterial(val) {
    this.material = val;
    this.root.querySelectorAll('[data-group="material"] .tmd-pill').forEach(b => {
      b.classList.toggle('active', b.dataset.value === val);
    });
  }

  setBgColor(hex) {
    this.bgColor = hex;
    const input = this.root.querySelector('#tmd-bg-color');
    if (input && input.value.toLowerCase() !== hex.toLowerCase()) input.value = hex;
    this.canvas.backgroundColor = hex;
    this.canvas.requestRenderAll();
    this.scheduleAutosave();
  }

  setStitchColor(hex) {
    this.stitchColor = hex;
    // Подсветка активного свотча
    this.root.querySelectorAll('[data-group="stitch"] .tmd-swatch:not(.tmd-swatch-custom)').forEach(b => {
      b.classList.toggle('active', b.dataset.value.toLowerCase() === hex.toLowerCase());
    });
    // Синхронизируем custom color picker
    const custom = this.root.querySelector('#tmd-stitch-custom');
    if (custom) custom.value = hex;
    // Live-превью: border на canvas-container через CSS custom property
    const frame = this.root.querySelector('.tmd-canvas-frame');
    if (frame) frame.style.setProperty('--tmd-stitch-color', hex);
    this.scheduleAutosave();
  }

  // --- AUTO-SWITCH RIGHT PANEL TAB ---
  switchRightTab(tabName) {
    const rightPanel = this.root.querySelector('.tmd-right');
    if (!rightPanel) return;
    const tabs = rightPanel.querySelector('.tmd-tabs');
    if (!tabs) return;
    tabs.querySelectorAll('.tmd-tab').forEach(b => b.classList.toggle('active', b.dataset.tab === tabName));
    rightPanel.querySelectorAll('[data-content]').forEach(c => { c.hidden = c.dataset.content !== tabName; });
  }

  // --- EMPTY STATE ---
  updateEmptyState() {
    const el = this.root.querySelector('#tmd-empty-state');
    if (!el) return;
    const hasObjects = this.canvas.getObjects().some(o => !o.data?.tmdSystem);
    el.classList.toggle('visible', !hasObjects);
  }

  // --- DRAG & DROP ---
  setupDragDrop() {
    const frame = this.root.querySelector('#tmd-canvas-frame');
    const overlay = this.root.querySelector('#tmd-drop-overlay');
    if (!frame || !overlay) return;
    let dragCounter = 0;

    frame.addEventListener('dragenter', (e) => {
      e.preventDefault();
      dragCounter++;
      overlay.classList.add('visible');
    });
    frame.addEventListener('dragleave', (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter <= 0) { dragCounter = 0; overlay.classList.remove('visible'); }
    });
    frame.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    });
    frame.addEventListener('drop', (e) => {
      e.preventDefault();
      dragCounter = 0;
      overlay.classList.remove('visible');
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      // Only accept images
      const imageFile = Array.from(files).find(f => f.type.startsWith('image/'));
      if (imageFile) {
        this.handleUpload(imageFile);
      }
    });
  }

  // --- MOBILE BOTTOM SHEET (v0.10.5) ---
  setupMobileSheet() {
    const fabBar = this.root.querySelector('#tmd-mobile-fab-bar');
    const backdrop = this.root.querySelector('#tmd-sheet-backdrop');
    if (!fabBar || !backdrop) return;

    // FAB buttons → open sheet
    fabBar.querySelectorAll('.tmd-mobile-fab').forEach(btn => {
      btn.addEventListener('click', () => {
        const side = btn.dataset.sheet; // 'left' or 'right'
        this.openMobileSheet(side);
      });
    });

    // Backdrop → close
    backdrop.addEventListener('click', () => this.closeMobileSheet());

    // Escape key → close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._mobileSheetOpen) this.closeMobileSheet();
    });
  }

  openMobileSheet(side) {
    const panel = this.root.querySelector(`.tmd-${side}`);
    const backdrop = this.root.querySelector('#tmd-sheet-backdrop');
    if (!panel) return;

    // Close any existing sheet first
    this.closeMobileSheet();

    panel.classList.add('tmd-sheet-visible');
    backdrop.classList.add('visible');
    this._mobileSheetOpen = side;

    // Mark FAB as active
    const fab = this.root.querySelector(`.tmd-mobile-fab[data-sheet="${side}"]`);
    if (fab) fab.classList.add('active');
  }

  closeMobileSheet() {
    const backdrop = this.root.querySelector('#tmd-sheet-backdrop');
    this.root.querySelectorAll('.tmd-panel.tmd-sheet-visible').forEach(p => {
      p.classList.remove('tmd-sheet-visible');
    });
    if (backdrop) backdrop.classList.remove('visible');
    this.root.querySelectorAll('.tmd-mobile-fab.active').forEach(f => f.classList.remove('active'));
    this._mobileSheetOpen = null;
  }

  handleAction(action) {
    const map = {
      'add-text': () => this.addText(),
      'add-rect': () => this.addRect(),
      'add-circle': () => this.addCircle(),
      'add-line': () => this.addLine(),
      'undo': () => this.undo(),
      'redo': () => this.redo(),
      'duplicate': () => this.duplicate(),
      'delete': () => this.deleteSelected(),
      'front': () => this.bringForward(),
      'back': () => this.sendBackward(),
      'add-to-cart': () => this.addToCart(),
      'bg-reset': () => this.setBgColor('#ffffff'),
      'toggle-soft-proof': () => this.toggleSoftProof(),
      'show-specs': () => this.showSpecsModal(),
      'toggle-lock': () => this.toggleLockAspectRatio(),
      'rotate-landscape': () => this.requestLandscape(),
    };
    if (map[action]) map[action]();
  }

  // --- LOCK ASPECT RATIO ---
  toggleLockAspectRatio() {
    this.lockAspectRatio = !this.lockAspectRatio;
    const btn = this.root.querySelector('[data-action="toggle-lock"]');
    if (btn) {
      btn.classList.toggle('active', this.lockAspectRatio);
      // Меняем иконку замка
      const svg = this.lockAspectRatio
        ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>'
        : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 019.9-1"/></svg>';
      btn.querySelector('svg').outerHTML = svg;
    }
  }

  // --- FULLSCREEN (mobile) — hides browser address bar ---
  async requestLandscape() {
    const el = this.root;
    const hint = this.root.querySelector('#tmd-rotate-hint');
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Method 1: Native Fullscreen API (Android Chrome, Samsung Internet)
    const fsMethod = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (fsMethod) {
      try {
        await fsMethod.call(el);
        // Try to lock landscape orientation
        if (screen.orientation && screen.orientation.lock) {
          try { await screen.orientation.lock('landscape'); } catch (_) {}
        }
        if (hint) hint.style.display = 'none';
        setTimeout(() => this.applyResponsiveCanvas(), 300);

        // Listen for fullscreen exit
        const onFsChange = () => {
          if (!document.fullscreenElement && !document.webkitFullscreenElement) {
            if (hint) hint.style.display = '';
            if (screen.orientation && screen.orientation.unlock) {
              try { screen.orientation.unlock(); } catch (_) {}
            }
            document.body.classList.remove('tmd-immersive');
            setTimeout(() => this.applyResponsiveCanvas(), 300);
          }
        };
        document.addEventListener('fullscreenchange', onFsChange, { once: true });
        document.addEventListener('webkitfullscreenchange', onFsChange, { once: true });
        return;
      } catch (_) {}
    }

    // Method 2: iOS Safari — scroll trick to minimize address bar + immersive mode
    if (isIOS) {
      // Add body padding to enable scroll, then scroll down to hide address bar
      document.body.classList.add('tmd-immersive');
      document.body.style.minHeight = (window.innerHeight + 100) + 'px';
      window.scrollTo({ top: 1, behavior: 'instant' });
      // Wait for Safari to collapse the bar, then reset
      setTimeout(() => {
        document.body.style.minHeight = '';
        if (hint) hint.style.display = 'none';
        this.applyResponsiveCanvas();
      }, 400);
      // Show exit button
      this._showExitImmersive();
      return;
    }

    // Fallback: manual hint
    this.showRotateToast();
  }

  _showExitImmersive() {
    // Floating "exit fullscreen" button for iOS
    let exitBtn = this.root.querySelector('#tmd-exit-immersive');
    if (exitBtn) return;
    exitBtn = document.createElement('button');
    exitBtn.id = 'tmd-exit-immersive';
    exitBtn.className = 'tmd-exit-immersive-btn';
    exitBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 14h6v6m10-10h-6V4m0 6L20 4M4 20l6-6"/></svg>';
    exitBtn.title = 'Выйти из полного экрана';
    exitBtn.addEventListener('click', () => {
      document.body.classList.remove('tmd-immersive');
      const hint = this.root.querySelector('#tmd-rotate-hint');
      if (hint) hint.style.display = '';
      exitBtn.remove();
      setTimeout(() => this.applyResponsiveCanvas(), 200);
    });
    this.root.appendChild(exitBtn);
  }

  showRotateToast() {
    // For iOS / browsers that don't support fullscreen+orientation lock
    const toast = document.createElement('div');
    toast.className = 'tmd-toast';
    toast.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="6" width="22" height="12" rx="2"/><line x1="8" y1="12" x2="8.01" y2="12"/></svg>
      <span>Поверните телефон горизонтально</span>
    `;
    this.root.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('visible'));
    setTimeout(() => {
      toast.classList.remove('visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // --- DIMENSIONS DISPLAY ---
  // Вычисляет реальные размеры объекта в мм и показывает в баре под канвасом
  getObjectDimensionsMm(obj) {
    if (!obj) return null;
    const w = (obj.width || 0) * Math.abs(obj.scaleX || 1);
    const h = (obj.height || 0) * Math.abs(obj.scaleY || 1);
    const mmPerDisplayPx = 1 / this.displayPxPerMm;
    const wMm = w * mmPerDisplayPx;
    const hMm = h * mmPerDisplayPx;
    return { wPx: Math.round(w), hPx: Math.round(h), wMm: Math.round(wMm * 10) / 10, hMm: Math.round(hMm * 10) / 10 };
  }

  getAspectRatioLabel(w, h) {
    if (!w || !h) return '';
    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const g = gcd(Math.round(w), Math.round(h));
    const rw = Math.round(w / g);
    const rh = Math.round(h / g);
    // Если получилось слишком громоздкое соотношение — показываем десятичное
    if (rw > 50 || rh > 50) return `${(w / h).toFixed(2)}:1`;
    return `${rw}:${rh}`;
  }

  updateDimensionsDisplay(obj) {
    const bar = this.root.querySelector('#tmd-dimensions-bar');
    if (!bar) return;
    if (!obj || (obj.data && obj.data.tmdSystem)) {
      bar.innerHTML = '';
      bar.classList.remove('visible');
      return;
    }
    const d = this.getObjectDimensionsMm(obj);
    if (!d) return;
    const ratio = this.getAspectRatioLabel(d.wMm, d.hMm);
    const printWPx = Math.round(d.wPx * this.printMultiplier);
    const printHPx = Math.round(d.hPx * this.printMultiplier);
    bar.innerHTML = `<span class="tmd-dim-size">${d.wMm}×${d.hMm} мм</span>` +
      `<span class="tmd-dim-px">${printWPx}×${printHPx} px</span>` +
      (ratio ? `<span class="tmd-dim-ratio">${ratio}</span>` : '');
    bar.classList.add('visible');
  }

  // --- TEMPLATE SWITCHER ---
  selectTemplate(templateId) {
    const tpl = this.templates.find(t => t.template_id === templateId);
    if (!tpl) return;
    this.currentTemplateId = tpl.template_id;
    this.currentProductId = tpl.product_id || 0;
    // bleed/safe/thickness/dpi могут отличаться между шаблонами — обновляем
    if (tpl.bleed_mm != null) this.bleedMm = tpl.bleed_mm;
    if (tpl.safe_mm != null) this.safeMm = tpl.safe_mm;
    if (tpl.thickness_mm != null) {
      this.thicknessMm = tpl.thickness_mm;
      this.root.querySelectorAll('[data-group="thickness"] .tmd-pill').forEach(b => {
        b.classList.toggle('active', Number(b.dataset.value) === this.thicknessMm);
      });
    }
    if (tpl.print_dpi) this.printDpi = tpl.print_dpi;
    // Размер коврика
    this.setSize(tpl.width_mm, tpl.height_mm);
    // Цена (обновить видимые элементы)
    this.refreshPriceUi();
    // Сбросить saved ids — новый шаблон = новый дизайн
    this.savedIds = null;
  }

  refreshPriceUi() {
    const priceHtml = this.currentPriceHtml();
    const price1 = this.root.querySelector('.tmd-cta-box .tmd-price');
    if (price1) price1.innerHTML = priceHtml;
    const price2 = this.root.querySelector('#tmd-price-dynamic');
    if (price2) price2.innerHTML = priceHtml;
  }

  // --- SOFT-PROOF (грубая CSS-симуляция CMYK) ---
  toggleSoftProof() {
    this.softProofOn = !this.softProofOn;
    const frame = this.root.querySelector('.tmd-canvas-frame');
    const btn = this.root.querySelector('[data-action="toggle-soft-proof"]');
    if (frame) frame.style.filter = this.softProofOn ? SOFT_PROOF_FILTER : 'none';
    if (btn) btn.classList.toggle('active', this.softProofOn);
  }

  // --- ТЗ-МОДАЛКА (требования к макету) ---
  showSpecsModal() {
    const specs = this.cfg.factory_specs || {};
    const notes = specs.notes || [];
    const w = this.widthMm;
    const h = this.heightMm;
    const bleed = this.bleedMm;
    const safe = this.safeMm;
    const printW = Math.round(((w + bleed*2) / INCH_MM) * this.printDpi);
    const printH = Math.round(((h + bleed*2) / INCH_MM) * this.printDpi);

    const html = `
      <div class="tmd-modal-backdrop" data-action="close-modal">
        <div class="tmd-modal" role="dialog" aria-modal="true" onclick="event.stopPropagation()">
          <button class="tmd-modal-close" data-action="close-modal" title="Закрыть">✕</button>
          <h2>Требования к макету (фабрика Токи-Токи)</h2>

          <div class="tmd-specs-grid">
            <div><b>Формат:</b></div><div>${this.escape(specs.format || 'TIFF или PDF')}</div>
            <div><b>Цветовая модель:</b></div><div>${this.escape(specs.color_mode || 'CMYK')}</div>
            <div><b>ICC-профиль:</b></div><div>${this.escape(specs.icc_profile || 'Coated FOGRA39')}</div>
            <div><b>DPI:</b></div><div>${specs.dpi || 300}</div>
            <div><b>Макс. плотность краски:</b></div><div>${this.escape(specs.max_ink_density || '300%')}</div>
            <div><b>Чёрный для вектора/текста:</b></div><div>${this.escape(specs.black_vector || 'K100 (C0 M0 Y0 K100)')}</div>
            <div><b>Bleed (вылет):</b></div><div>${bleed} мм со всех сторон</div>
            <div><b>Safe-zone (безопасное поле):</b></div><div>${safe} мм от края</div>
            <div><b>Размер вашего коврика:</b></div><div>${w}×${h} мм</div>
            <div><b>Итоговый файл (с bleed):</b></div><div>${w + bleed*2}×${h + bleed*2} мм · ${printW}×${printH} px</div>
          </div>

          <h3>Safe-zone</h3>
          <p class="tmd-modal-text">
            Красная пунктирная рамка на холсте — это <b>линия реза</b>. Всё, что за ней — уйдёт под обрезку (bleed).
            Важный контент (логотип, текст, лицо) держите минимум в <b>${safe} мм</b> внутрь от этой рамки —
            иначе его может срезать на производстве (резка не идеальна).
          </p>

          <h3>Что сделает наш конвертер автоматически</h3>
          <ul class="tmd-modal-list">
            <li>Преобразует RGB → CMYK по профилю FOGRA39 (relative colorimetric + BPC).</li>
            <li>Векторный чёрный текст и фигуры с чистым #000 — принудительно в K100.</li>
            <li>Сохранит в TIFF 8-bit, LZW, 300 DPI с embedded ICC-профилем.</li>
            <li>Добавит bleed 7.5 мм (фон дотянется до края).</li>
          </ul>

          <h3>Рекомендации</h3>
          <ul class="tmd-modal-list">
            ${notes.map(n => `<li>${this.escape(n)}</li>`).join('')}
          </ul>

          <div class="tmd-modal-footer">
            <button class="tmd-cta" data-action="close-modal">Понятно, продолжить</button>
          </div>
        </div>
      </div>
    `;
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    const modal = wrap.firstElementChild;
    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => {
      const a = e.target.closest('[data-action="close-modal"]');
      if (a) modal.remove();
    });
    // Esc — закрыть
    const onKey = (e) => {
      if (e.key === 'Escape') { modal.remove(); document.removeEventListener('keydown', onKey); }
    };
    document.addEventListener('keydown', onKey);
  }

  // --- UPLOAD + DPI CHECK ---
  // Phase 2b.6: sRGB-нормализация пользовательского изображения на сервере ДО того,
  // как оно попадёт в Fabric.Canvas. Это гарантирует корректную CMYK-конверсию на
  // финальном шаге (иначе Adobe RGB / wide-gamut JPEG → искажённый цвет на печати).
  handleUpload(file) {
    const hint = this.root.querySelector('#tmd-upload-hint');
    if (hint) {
      hint.className = 'tmd-hint';
      hint.textContent = 'Нормализация цвета (sRGB)…';
    }

    const fd = new FormData();
    fd.append('action', 'tmd_normalize_upload');
    fd.append('nonce', this.cfg.nonce);
    fd.append('file', file);

    fetch(this.cfg.ajax_url, { method: 'POST', body: fd, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((resp) => {
        if (!resp || !resp.success) {
          const msg = (resp && resp.data && resp.data.msg) ? resp.data.msg : 'Ошибка нормализации';
          if (hint) {
            hint.className = 'tmd-hint tmd-hint-warning';
            hint.textContent = '⚠️ ' + msg;
          }
          return;
        }
        const dataUrl = resp.data.data_url;
        fabric.FabricImage.fromURL(dataUrl).then((img) => {
          // Scale-to-COVER: картинка заполняет весь коврик, лишнее обрезается safe-zone-ом.
          // Пользователь может потом подвинуть/уменьшить (Fabric даёт это из коробки).
          const scale = Math.max(
            this.displayWidth / img.width,
            this.displayHeight / img.height
          );
          img.scale(scale);
          img.set({
            left: (this.displayWidth - img.width * scale) / 2,
            top: (this.displayHeight - img.height * scale) / 2,
            data: { tmdType: 'image', name: file.name, tmdNormalized: true },
          });
          this.canvas.add(img);
          this.canvas.setActiveObject(img);
          this.canvas.requestRenderAll();
          this.updateDpiHint();
        });
      })
      .catch((err) => {
        if (hint) {
          hint.className = 'tmd-hint tmd-hint-warning';
          hint.textContent = '⚠️ Сетевая ошибка: ' + (err && err.message ? err.message : err);
        }
      });
  }

  // Пересчёт DPI-подсказки для #tmd-upload-hint.
  // Смотрит активную картинку, иначе — первую картинку на канвасе,
  // иначе — показывает дефолтный текст-призыв.
  updateDpiHint() {
    const hint = this.root.querySelector('#tmd-upload-hint');
    if (hint) {
      const active = this.canvas.getActiveObject();
      let img = (active && (active.type === 'image' || active.type === 'Image')) ? active : null;
      if (!img) {
        img = this.canvas.getObjects().find((o) =>
          !(o.data && o.data.tmdSystem) && (o.type === 'image' || o.type === 'Image')
        );
      }
      if (!img) {
        hint.className = 'tmd-hint';
        hint.textContent = 'PNG / JPG. Рекомендуется ≥' + this.dpiRecommend + ' DPI на печать.';
      } else {
        const effectiveDpi = img.width / (this.widthMm / INCH_MM);
        const base = img.width + '×' + img.height + ' px → ~' + Math.round(effectiveDpi) + ' DPI на печать';
        let text, cls;
        if (effectiveDpi < this.dpiMin) {
          text = '⚠️ ' + base + '. Слишком низкое — нельзя в печать (нужно ≥' + this.dpiMin + ' DPI).';
          cls = 'tmd-hint tmd-hint-warning';
        } else if (effectiveDpi < this.dpiRecommend) {
          text = 'ℹ️ ' + base + '. Рекомендуем ≥' + this.dpiRecommend + ' DPI.';
          cls = 'tmd-hint';
        } else {
          text = '✓ ' + base + '. Качество печати отличное.';
          cls = 'tmd-hint tmd-hint-ok';
        }
        hint.className = cls;
        hint.textContent = text;
      }
    }
    // Любое изменение DPI картинок может разблокировать/заблокировать save
    this.updateSaveButton();
  }

  // Минимальное DPI среди всех картинок на канвасе. Infinity если картинок нет.
  computeLowestUploadDpi() {
    let lowest = Infinity;
    this.canvas.getObjects().forEach((o) => {
      if (o.data && o.data.tmdSystem) return;
      if (o.type === 'image' || o.type === 'Image') {
        const dpi = o.width / (this.widthMm / INCH_MM);
        if (dpi < lowest) lowest = dpi;
      }
    });
    return lowest;
  }

  // Блокирует CTA «Добавить в корзину», если: (а) хотя бы одна картинка <dpiMin DPI,
  // или (б) клиент не подтвердил макет галочкой. Показывает причину блокировки.
  updateSaveButton() {
    const cta = this.root.querySelector('.tmd-cta[data-action="add-to-cart"]');
    const note = this.root.querySelector('#tmd-cta-block-note');
    if (!cta) return;
    const lowestDpi = this.computeLowestUploadDpi();
    const dpiOk = !isFinite(lowestDpi) || lowestDpi >= this.dpiMin;
    const approvedOk = !!this.designApproved;
    const reasons = [];
    if (!dpiOk) reasons.push('Замените изображение на ≥' + this.dpiMin + ' DPI (сейчас ~' + Math.round(lowestDpi) + ')');
    if (!approvedOk) reasons.push('Подтвердите макет галочкой');
    cta.disabled = reasons.length > 0;
    if (note) {
      note.textContent = reasons.join(' · ');
      note.style.display = reasons.length ? '' : 'none';
    }
  }

  // --- ADD OBJECTS ---
  addText() {
    const t = new fabric.Textbox('Ваш текст', {
      left: this.displayWidth / 2 - 100,
      top: this.displayHeight / 2 - 20,
      width: 200,
      fontSize: 40,
      fontFamily: 'Arial',
      fill: '#1e1e2e',
      textAlign: 'center',
      data: { tmdType: 'text' },
    });
    this.canvas.add(t);
    this.canvas.setActiveObject(t);
    this.canvas.requestRenderAll();
  }

  addRect() {
    const r = new fabric.Rect({
      left: this.displayWidth / 2 - 60,
      top: this.displayHeight / 2 - 60,
      width: 120, height: 120,
      fill: '#4285f4',
      data: { tmdType: 'rect' },
    });
    this.canvas.add(r); this.canvas.setActiveObject(r); this.canvas.requestRenderAll();
  }

  addCircle() {
    const c = new fabric.Circle({
      left: this.displayWidth / 2 - 60,
      top: this.displayHeight / 2 - 60,
      radius: 60,
      fill: '#34a853',
      data: { tmdType: 'circle' },
    });
    this.canvas.add(c); this.canvas.setActiveObject(c); this.canvas.requestRenderAll();
  }

  addLine() {
    const l = new fabric.Line([0, 0, 200, 0], {
      left: this.displayWidth / 2 - 100,
      top: this.displayHeight / 2,
      stroke: '#1e1e2e',
      strokeWidth: 4,
      data: { tmdType: 'line' },
    });
    this.canvas.add(l); this.canvas.setActiveObject(l); this.canvas.requestRenderAll();
  }

  // --- TOOLBAR STATE (undo/redo disabled) ---
  updateToolbarState() {
    const undoBtn = this.root.querySelector('[data-action="undo"]');
    const redoBtn = this.root.querySelector('[data-action="redo"]');
    if (undoBtn) undoBtn.disabled = this.historyIndex <= 0;
    if (redoBtn) redoBtn.disabled = this.historyIndex >= this.history.length - 1;
  }

  // --- HISTORY + OBJECT OPS ---
  undo() {
    if (this.historyIndex <= 0) return;
    this.historyIndex--;
    this.restore(this.history[this.historyIndex]);
  }

  redo() {
    if (this.historyIndex >= this.history.length - 1) return;
    this.historyIndex++;
    this.restore(this.history[this.historyIndex]);
  }

  restore(json) {
    this.isRestoring = true;
    this.canvas.loadFromJSON(json).then(() => {
      if (!this.canvas.getObjects().includes(this.safeZone)) {
        this.canvas.add(this.safeZone);
      }
      this.canvas.requestRenderAll();
      this.isRestoring = false;
      this.refreshLayers();
      this.updateToolbarState();
    });
  }

  duplicate() {
    const obj = this.canvas.getActiveObject();
    if (!obj || obj.data?.tmdSystem) return;
    obj.clone().then((clone) => {
      clone.set({ left: (obj.left || 0) + 20, top: (obj.top || 0) + 20 });
      this.canvas.add(clone);
      this.canvas.setActiveObject(clone);
      this.canvas.requestRenderAll();
    });
  }

  deleteSelected() {
    const objs = this.canvas.getActiveObjects();
    objs.forEach((o) => {
      if (o.data?.tmdSystem) return;
      this.canvas.remove(o);
    });
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();
  }

  bringForward() {
    const obj = this.canvas.getActiveObject(); if (!obj) return;
    this.canvas.bringObjectForward(obj); this.canvas.requestRenderAll();
  }

  sendBackward() {
    const obj = this.canvas.getActiveObject(); if (!obj) return;
    this.canvas.sendObjectBackwards(obj); this.canvas.requestRenderAll();
  }

  // --- LAYERS LIST ---
  refreshLayers() {
    this.renderFilesList();
    const ul = this.root.querySelector('#tmd-layers-list');
    if (!ul) return;
    const objs = this.canvas.getObjects().filter(o => !o.data?.tmdSystem);
    if (objs.length === 0) {
      ul.innerHTML = '<li class="tmd-empty">Пока пусто</li>';
      return;
    }
    const icons = { text: 'T', image: '🖼', rect: '▭', circle: '◯', line: '╱' };
    ul.innerHTML = objs.slice().reverse().map((o, idx) => {
      const type = o.data?.tmdType || o.type || 'obj';
      const label = type === 'text' ? `«${(o.text || '').slice(0,20)}»`
                  : type === 'image' ? (o.data?.name || 'Картинка')
                  : type === 'rect' ? 'Прямоугольник'
                  : type === 'circle' ? 'Круг'
                  : type === 'line' ? 'Линия' : type;
      return `<li class="tmd-layer" data-idx="${objs.length - 1 - idx}">
        <span class="tmd-layer-icon">${icons[type] || '•'}</span>
        <span class="tmd-layer-label">${this.escape(label)}</span>
        <button class="tmd-layer-delete" data-action-layer="delete">✕</button>
      </li>`;
    }).join('');
    ul.querySelectorAll('.tmd-layer').forEach(li => {
      li.addEventListener('click', (e) => {
        if (e.target.closest('[data-action-layer]')) return;
        const idx = parseInt(li.dataset.idx);
        const obj = this.canvas.getObjects().filter(o => !o.data?.tmdSystem)[idx];
        if (obj) { this.canvas.setActiveObject(obj); this.canvas.requestRenderAll(); }
      });
      li.querySelector('.tmd-layer-delete')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(li.dataset.idx);
        const obj = this.canvas.getObjects().filter(o => !o.data?.tmdSystem)[idx];
        if (obj) { this.canvas.remove(obj); this.canvas.requestRenderAll(); }
      });
    });
  }

  // --- OBJECT PROPERTIES (правая вкладка «Объект») ---
  renderObjectPanel(obj) {
    const host = this.root.querySelector('#tmd-object-props');
    if (!host) return;
    if (!obj) {
      host.innerHTML = '<p class="tmd-empty">Выберите объект на холсте, чтобы изменить его свойства.</p>';
      return;
    }
    const type = obj.data?.tmdType || obj.type;
    let html = '';
    if (type === 'text' || obj.type === 'textbox' || obj.type === 'i-text') {
      html = `
        <div class="tmd-field">
          <label>Текст</label>
          <input type="text" data-prop="text" value="${this.escape(obj.text || '')}">
        </div>
        <div class="tmd-field">
          <label>Размер шрифта</label>
          <input type="number" data-prop="fontSize" min="8" max="300" value="${Math.round(obj.fontSize || 40)}">
        </div>
        <div class="tmd-field">
          <label>Шрифт</label>
          <select data-prop="fontFamily">
            ${['Arial','Helvetica','Georgia','Times New Roman','Courier New','Verdana','Impact','Comic Sans MS']
              .map(f => `<option ${f === obj.fontFamily ? 'selected' : ''}>${f}</option>`).join('')}
          </select>
        </div>
        <div class="tmd-field">
          <label>Цвет</label>
          <input type="color" data-prop="fill" value="${this.toHex(obj.fill)}">
        </div>
        <div class="tmd-field">
          <label>Стиль</label>
          <div class="tmd-row">
            <button class="tmd-toggle ${obj.fontWeight === 'bold' ? 'active':''}" data-toggle="fontWeight" data-on="bold" data-off="normal"><b>B</b></button>
            <button class="tmd-toggle ${obj.fontStyle === 'italic' ? 'active':''}" data-toggle="fontStyle" data-on="italic" data-off="normal"><i>I</i></button>
          </div>
        </div>
      `;
    } else if (type === 'rect' || type === 'circle') {
      html = `
        <div class="tmd-field"><label>Заливка</label>
          <input type="color" data-prop="fill" value="${this.toHex(obj.fill)}"></div>
        <div class="tmd-field"><label>Обводка</label>
          <input type="color" data-prop="stroke" value="${this.toHex(obj.stroke || '#000000')}"></div>
        <div class="tmd-field"><label>Толщина обводки</label>
          <input type="number" data-prop="strokeWidth" min="0" max="50" value="${obj.strokeWidth || 0}"></div>
      `;
    } else if (type === 'line') {
      html = `
        <div class="tmd-field"><label>Цвет линии</label>
          <input type="color" data-prop="stroke" value="${this.toHex(obj.stroke)}"></div>
        <div class="tmd-field"><label>Толщина</label>
          <input type="number" data-prop="strokeWidth" min="1" max="50" value="${obj.strokeWidth || 1}"></div>
      `;
    } else if (type === 'image' || obj.type === 'image') {
      html = `
        <div class="tmd-field"><label>Имя файла</label>
          <div class="tmd-field-value">${this.escape(obj.data?.name || '—')}</div></div>
        <div class="tmd-field"><label>Исходный размер</label>
          <div class="tmd-field-value">${Math.round(obj.width)}×${Math.round(obj.height)} px</div></div>
      `;
    }
    // Размеры объекта (реальные мм + px для печати + пропорция)
    const dims = this.getObjectDimensionsMm(obj);
    if (dims) {
      const printW = Math.round(dims.wPx * this.printMultiplier);
      const printH = Math.round(dims.hPx * this.printMultiplier);
      const ratio = this.getAspectRatioLabel(dims.wMm, dims.hMm);
      html += `
        <div class="tmd-field tmd-dims-block">
          <div class="tmd-field-label">Размеры</div>
          <div class="tmd-dims-grid">
            <div class="tmd-dim-cell"><span class="tmd-dim-val">${dims.wMm}×${dims.hMm}</span><span class="tmd-dim-unit">мм</span></div>
            <div class="tmd-dim-cell"><span class="tmd-dim-val">${printW}×${printH}</span><span class="tmd-dim-unit">px (печать)</span></div>
            ${ratio ? `<div class="tmd-dim-cell"><span class="tmd-dim-val">${ratio}</span><span class="tmd-dim-unit">пропорция</span></div>` : ''}
          </div>
        </div>
      `;
    }
    // Общие поля: позиция + масштаб/угол
    html += `
      <div class="tmd-field"><label>Поворот</label>
        <input type="number" data-prop="angle" min="-180" max="180" value="${Math.round(obj.angle || 0)}"></div>
      <div class="tmd-field tmd-field-inline">
        <div><label>X</label><input type="number" data-prop="left" value="${Math.round(obj.left || 0)}"></div>
        <div><label>Y</label><input type="number" data-prop="top" value="${Math.round(obj.top || 0)}"></div>
      </div>
    `;
    host.innerHTML = html;

    host.querySelectorAll('[data-prop]').forEach((inp) => {
      inp.addEventListener('input', () => {
        const prop = inp.dataset.prop;
        let val = inp.value;
        if (inp.type === 'number') val = parseFloat(val) || 0;
        obj.set(prop, val);
        obj.setCoords();
        this.canvas.requestRenderAll();
      });
      inp.addEventListener('change', () => this.canvas.fire('object:modified', { target: obj }));
    });
    host.querySelectorAll('[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const prop = btn.dataset.toggle;
        const next = obj.get(prop) === btn.dataset.on ? btn.dataset.off : btn.dataset.on;
        obj.set(prop, next);
        btn.classList.toggle('active');
        this.canvas.requestRenderAll();
        this.canvas.fire('object:modified', { target: obj });
      });
    });
  }

  // --- VECTOR MASK GENERATOR ---
  // Рендерит только чёрные векторные объекты (text / rect / circle / line с fill/stroke = #000)
  // на прозрачном канвасе. Серверный конвертер использует эту маску для принудительного K100.
  // Если чёрных векторов нет — возвращает null (конвертер работает без маски).
  generateVectorMask() {
    const blackObjects = [];
    const isBlack = (c) => {
      if (!c) return false;
      if (typeof c !== 'string') return false;
      const s = c.trim().toLowerCase();
      return s === '#000' || s === '#000000' || s === 'black' ||
             s === 'rgb(0,0,0)' || s === 'rgb(0, 0, 0)' ||
             s === 'rgba(0,0,0,1)' || s === 'rgba(0, 0, 0, 1)';
    };
    this.canvas.getObjects().forEach((o) => {
      if (o.data && o.data.tmdSystem) return;
      const type = o.type || o.data?.tmdType;
      // Исключаем image — только векторные элементы
      if (type === 'image' || type === 'Image') return;
      const fillBlack = isBlack(o.fill);
      const strokeBlack = isBlack(o.stroke) && (o.strokeWidth || 0) > 0;
      if (fillBlack || strokeBlack) blackObjects.push({ obj: o, fillBlack, strokeBlack });
    });
    if (blackObjects.length === 0) return null;

    // Создаём скрытый Fabric канвас в целевом (print) разрешении и рендерим туда клоны объектов
    // на прозрачном фоне. Чёрные = белые пиксели (для маски), остальное прозрачно.
    const printW = this.printWidthPx;
    const printH = this.printHeightPx;
    const scale = this.printMultiplier;

    const off = new fabric.StaticCanvas(null, {
      width: printW,
      height: printH,
      backgroundColor: '#000000', // чёрный фон (в маске = НЕ K100)
      enableRetinaScaling: false,
    });

    // Клонируем каждый чёрный объект и переключаем его цвет на белый (для маски)
    const clonePromises = blackObjects.map(({ obj, fillBlack, strokeBlack }) => obj.clone().then((clone) => {
      clone.set({
        left: (obj.left || 0) * scale,
        top: (obj.top || 0) * scale,
        scaleX: (obj.scaleX || 1) * scale,
        scaleY: (obj.scaleY || 1) * scale,
        fill: fillBlack ? '#ffffff' : 'transparent',
        stroke: strokeBlack ? '#ffffff' : null,
        strokeWidth: strokeBlack ? (obj.strokeWidth || 0) * scale : 0,
        excludeFromExport: false,
        selectable: false,
      });
      off.add(clone);
    }));

    return Promise.all(clonePromises).then(() => {
      off.renderAll();
      const dataUrl = off.toDataURL({ format: 'png', multiplier: 1 });
      off.dispose();
      return dataUrl;
    });
  }

  // --- SAVE + ADD TO CART ---
  async save() {
    const status = this.root.querySelector('#tmd-save-status');
    // Защита от обхода CTA-disabled (devtools, кривой клиент):
    // проверяем DPI и approval здесь же, отказываем с понятным сообщением.
    const lowestDpi = this.computeLowestUploadDpi();
    if (isFinite(lowestDpi) && lowestDpi < this.dpiMin) {
      const msg = 'Разрешение изображения (~' + Math.round(lowestDpi) + ' DPI) ниже минимума ' + this.dpiMin + ' DPI. Загрузите файл большего размера.';
      if (status) { status.className = 'tmd-save-status tmd-save-err'; status.textContent = msg; }
      throw new Error(msg);
    }
    if (!this.designApproved) {
      const msg = 'Подтвердите макет галочкой «Я проверил(а) макет» перед сохранением.';
      if (status) { status.className = 'tmd-save-status tmd-save-err'; status.textContent = msg; }
      throw new Error(msg);
    }
    status.className = 'tmd-save-status';
    status.textContent = 'Сохраняем дизайн…';

    // Прячем safe-zone и выключаем soft-proof на время экспорта
    const wasSoftProof = this.softProofOn;
    if (wasSoftProof) {
      const frame = this.root.querySelector('.tmd-canvas-frame');
      if (frame) frame.style.filter = 'none';
    }
    this.safeZone.visible = false;
    if (this.safeInner) this.safeInner.visible = false;
    this.canvas.requestRenderAll();

    try {
      const previewB64 = this.canvas.toDataURL({ format: 'png', multiplier: 1 });
      const printB64 = this.canvas.toDataURL({ format: 'png', multiplier: this.printMultiplier });
      const stateJson = JSON.stringify(this.canvas.toJSON(['data']));

      // Vector mask для K100 overlay — может вернуть null если нет чёрных векторов
      let vectorMaskB64 = '';
      try {
        const mask = await this.generateVectorMask();
        if (mask) vectorMaskB64 = mask;
      } catch (e) {
        console.warn('[TMD] vector mask gen failed', e);
      }

      const form = new FormData();
      form.append('action', 'tmd_save_design');
      form.append('nonce', this.cfg.nonce);
      form.append('preview_b64', previewB64);
      form.append('print_b64', printB64);
      if (vectorMaskB64) form.append('vector_mask_b64', vectorMaskB64);
      form.append('state_json', stateJson);
      form.append('template_id', String(this.currentTemplateId));

      const res = await fetch(this.cfg.ajax_url, { method: 'POST', body: form });
      const json = await res.json();
      if (!json.success) throw new Error(json.data?.msg || 'AJAX error');
      this.savedIds = json.data;

      let statusText = `✓ Дизайн сохранён (превью #${json.data.preview_id}, печать #${json.data.print_id}`;
      if (json.data.tiff_id) {
        statusText += `, TIFF CMYK #${json.data.tiff_id}`;
      } else if (json.data.tiff_error) {
        statusText += `, TIFF: ошибка — ${json.data.tiff_error}`;
      }
      statusText += ')';
      status.className = 'tmd-save-status tmd-save-ok';
      status.textContent = statusText;
      return json.data;
    } catch (err) {
      status.className = 'tmd-save-status tmd-save-err';
      status.textContent = `Ошибка сохранения: ${err.message}`;
      console.error('[TMD] save failed', err);
      throw err;
    } finally {
      this.safeZone.visible = true;
      if (this.safeInner) this.safeInner.visible = true;
      if (wasSoftProof) {
        const frame = this.root.querySelector('.tmd-canvas-frame');
        if (frame) frame.style.filter = SOFT_PROOF_FILTER;
      }
      this.canvas.requestRenderAll();
    }
  }

  async addToCart() {
    // Приоритет: текущий шаблон → cfg.product_id
    const productId = this.currentProductId || this.cfg.product_id;
    if (!productId) {
      alert('Товар не привязан к шаблону коврика. Администратор должен связать их в WordPress.');
      return;
    }
    const objs = this.canvas.getObjects().filter(o => !o.data?.tmdSystem);
    if (objs.length === 0) {
      if (!confirm('На коврике пока нет дизайна. Всё равно добавить в корзину?')) return;
    }

    const cta = this.root.querySelector('.tmd-cta');
    cta.disabled = true;
    const ctaText = cta.querySelector('span');
    const origText = ctaText.textContent;
    ctaText.textContent = 'Сохраняем…';

    try {
      const ids = await this.save();
      ctaText.textContent = 'Добавляем в корзину…';
      const form = new FormData();
      form.append('add-to-cart', String(productId));
      form.append('quantity', '1');
      form.append('tmd_preview_id', ids.preview_id);
      form.append('tmd_print_id', ids.print_id);
      if (ids.tiff_id) form.append('tmd_print_tiff_id', ids.tiff_id);
      form.append('tmd_template_id', String(this.currentTemplateId));
      form.append('tmd_state_json', JSON.stringify(this.canvas.toJSON(['data'])));
      form.append('tmd_thickness_mm', String(this.thicknessMm));
      form.append('tmd_material', this.material);
      form.append('tmd_bg_color', this.bgColor);
      form.append('tmd_stitch_color', this.stitchColor);
      form.append('tmd_width_mm', String(this.widthMm));
      form.append('tmd_height_mm', String(this.heightMm));
      form.append('tmd_approval', this.designApproved ? '1' : '0');
      const res = await fetch('/shop/', { method: 'POST', body: form, credentials: 'include' });
      if (res.ok) {
        ctaText.textContent = '✓ В корзине! Переходим…';
        setTimeout(() => {
          location.href = this.cfg.product?.shop_url || '/cart/';
        }, 600);
      } else {
        throw new Error('HTTP ' + res.status);
      }
    } catch (err) {
      ctaText.textContent = origText;
      cta.disabled = false;
      alert('Не удалось добавить в корзину: ' + err.message);
      console.error(err);
    }
  }

  // --- UTILS ---
  escape(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]);
  }
  toHex(color) {
    if (!color || color === 'transparent') return '#000000';
    if (typeof color === 'string' && color.startsWith('#')) return color.length === 7 ? color : '#000000';
    // rgb(…)
    const m = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(color || '');
    if (m) return '#' + [m[1], m[2], m[3]].map(v => (+v).toString(16).padStart(2, '0')).join('');
    return '#000000';
  }


  // --- FILES TAB (v0.6) ---
  renderFilesList() {
    const container = this.root.querySelector('[data-content="files"]');
    if (!container) return;
    const images = this.canvas.getObjects().filter((o) => {
      if (o.data && o.data.tmdSystem) return false;
      return (o.type === 'image' || o.type === 'Image');
    });
    if (images.length === 0) {
      container.innerHTML = '<p class="tmd-hint">Загрузите изображение — оно появится здесь.</p>';
      return;
    }
    const items = images.map((img, i) => {
      const name = (img.data && img.data.name) ? img.data.name : ('Изображение ' + (i + 1));
      // miniature: fabric v6 FabricImage хранит img._element (HTMLImageElement)
      const src = (img._element && img._element.src)
        || (typeof img.getSrc === 'function' ? img.getSrc() : '');
      return '<div class="tmd-file-item" data-idx="' + i + '">'
        + '<img class="tmd-file-thumb" src="' + src + '" alt="">'
        + '<div class="tmd-file-meta">'
        +   '<div class="tmd-file-name">' + this.escape(name) + '</div>'
        +   '<div class="tmd-file-sub">' + img.width + '×' + img.height + ' px</div>'
        + '</div>'
        + '<button class="tmd-file-del" title="Удалить" data-del-idx="' + i + '">✕</button>'
        + '</div>';
    }).join('');
    container.innerHTML = items;
    // клик по items — выделить объект
    container.querySelectorAll('.tmd-file-item').forEach((el) => {
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-del-idx]')) return;
        const idx = Number(el.dataset.idx);
        const img = images[idx];
        if (img) {
          this.canvas.setActiveObject(img);
          this.canvas.requestRenderAll();
        }
      });
    });
    container.querySelectorAll('[data-del-idx]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = Number(btn.dataset.delIdx);
        const img = images[idx];
        if (img) {
          this.canvas.remove(img);
          this.canvas.discardActiveObject();
          this.canvas.requestRenderAll();
        }
      });
    });
  }



  // --- GESTURES: pinch-zoom + pan для тач-устройств (v0.9) ---
  setupCanvasGestures() {
    const canvas = this.canvas;
    const upper = canvas.upperCanvasEl;
    if (!upper) return;
    let pinchActive = false;
    let pinchStartDist = 0;
    let pinchStartZoom = 1;
    let panActive = false;
    let panStartX = 0;
    let panStartY = 0;
    let panStartVT = null;

    const dist = (t1, t2) => {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    upper.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        pinchActive = true;
        pinchStartDist = dist(e.touches[0], e.touches[1]);
        pinchStartZoom = canvas.getZoom();
        // отключаем выделение объектов
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      } else if (e.touches.length === 1 && canvas.getZoom() > 1.05) {
        // Pan только если уже зумировано — иначе мешает обычному drag объекта
        const target = canvas.findTarget(e, true);
        if (!target) {
          panActive = true;
          panStartX = e.touches[0].clientX;
          panStartY = e.touches[0].clientY;
          panStartVT = canvas.viewportTransform.slice();
        }
      }
    }, { passive: false });

    upper.addEventListener('touchmove', (e) => {
      if (pinchActive && e.touches.length === 2) {
        e.preventDefault();
        const d = dist(e.touches[0], e.touches[1]);
        let newZoom = pinchStartZoom * (d / pinchStartDist);
        newZoom = Math.min(Math.max(newZoom, 0.5), 4);
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        const rect = upper.getBoundingClientRect();
        const point = new fabric.Point(cx - rect.left, cy - rect.top);
        canvas.zoomToPoint(point, newZoom);
      } else if (panActive && e.touches.length === 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - panStartX;
        const dy = e.touches[0].clientY - panStartY;
        const vt = panStartVT.slice();
        vt[4] += dx;
        vt[5] += dy;
        canvas.setViewportTransform(vt);
      }
    }, { passive: false });

    upper.addEventListener('touchend', (e) => {
      if (e.touches.length < 2) pinchActive = false;
      if (e.touches.length === 0) panActive = false;
    });

    // Mouse-wheel zoom (десктоп)
    canvas.on('mouse:wheel', (opt) => {
      const delta = opt.e.deltaY;
      let zoom = canvas.getZoom();
      zoom *= 0.999 ** delta;
      zoom = Math.min(Math.max(zoom, 0.5), 4);
      canvas.zoomToPoint(new fabric.Point(opt.e.offsetX, opt.e.offsetY), zoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    });

    // Двойной тап / даблклик — сброс зума
    let lastTap = 0;
    upper.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTap < 300 && e.touches.length === 0) {
        this.resetZoom();
      }
      lastTap = now;
    });
    canvas.on('mouse:dblclick', () => this.resetZoom());
  }

  resetZoom() {
    this.canvas.setZoom(1);
    this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    this.canvas.requestRenderAll();
  }

  // --- ADAPTIVE CANVAS SIZING (v0.10.5 mobile-optimized) ---
  computeDisplayPxPerMm(widthMm, heightMm) {
    const w = widthMm || this.widthMm;
    const h = heightMm || this.heightMm;
    const vw = (window.innerWidth || document.documentElement.clientWidth || 800);
    const vh = (window.innerHeight || document.documentElement.clientHeight || 600);
    let avail;
    if (vw >= 901) {
      // Desktop 3-col: left(240) + canvas + right(280) + gaps
      avail = Math.max(this.MIN_DISPLAY, Math.min(vw - 240 - 280 - 80, this.MAX_DISPLAY_DESKTOP));
    } else {
      // Mobile/tablet: percentage-based sizing
      const isLandscape = vw > vh;
      // canvas-frame padding: landscape ~1.5vw*2, portrait ~4vw*2. Stitch: 6*2=12
      const framePadW = isLandscape ? Math.round(vw * 0.03) : Math.round(vw * 0.08);
      const availW = vw - framePadW - 12;
      // Height: landscape (no topbar, ~15vh UI) vs portrait (~25vh UI)
      const uiH = isLandscape ? Math.round(vh * 0.15) : Math.round(vh * 0.25);
      const availH = vh - uiH;
      // For landscape mats (w > h), fit to width; but also check height doesn't overflow
      const pxPerMmByW = availW / w;
      const pxPerMmByH = availH / h;
      const pxPerMm = Math.min(pxPerMmByW, pxPerMmByH);
      return Math.max(pxPerMm, this.MIN_DISPLAY / Math.max(w, h));
    }
    return avail / Math.max(w, h);
  }

  setupResponsiveCanvas() {
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.applyResponsiveCanvas(), 120);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
  }

  applyResponsiveCanvas() {
    const newPxPerMm = this.computeDisplayPxPerMm();
    if (Math.abs(newPxPerMm - this.displayPxPerMm) < 0.01) return; // нет изменений
    const oldDisplayWidth = this.displayWidth;
    const oldDisplayHeight = this.displayHeight;
    this.displayPxPerMm = newPxPerMm;
    this.displayWidth = Math.round(this.widthMm * newPxPerMm);
    this.displayHeight = Math.round(this.heightMm * newPxPerMm);
    this.printMultiplier = this.printWidthPx / this.displayWidth;
    const sx = this.displayWidth / oldDisplayWidth;
    const sy = this.displayHeight / oldDisplayHeight;
    this.isRestoring = true;
    this.canvas.getObjects().forEach((o) => {
      if (o.data && o.data.tmdSystem) return;
      const isImg = (o.type === 'image' || o.type === 'Image');
      if (isImg) {
        const cover = Math.max(this.displayWidth / o.width, this.displayHeight / o.height);
        o.set({
          scaleX: cover, scaleY: cover,
          left: (this.displayWidth - o.width * cover) / 2,
          top: (this.displayHeight - o.height * cover) / 2,
        });
      } else {
        const u = Math.sqrt(sx * sy);
        o.set({
          left: (o.left || 0) * sx,
          top: (o.top || 0) * sy,
          scaleX: (o.scaleX || 1) * u,
          scaleY: (o.scaleY || 1) * u,
        });
      }
      o.setCoords();
    });
    this.isRestoring = false;
    this.canvas.setDimensions({ width: this.displayWidth, height: this.displayHeight });
    this.resetSafeZone();
    this.canvas.requestRenderAll();
  }

  // --- AUTOSAVE (v0.4) ---
  autosaveKey() {
    const id = (this.cfg.product && this.cfg.product.id) || this.cfg.template_id || 'default';
    return 'tmd:state:' + id;
  }

  scheduleAutosave() {
    if (this.isRestoring) return;
    clearTimeout(this._autosaveT);
    this._autosaveT = setTimeout(() => this.saveAutostate(), 400);
  }

  saveAutostate() {
    try {
      const json = this.canvas.toJSON(['data']);
      // background/overlay — свойства шаблона, не пользовательские. Не сохраняем.
      if (json) {
        delete json.background;
        delete json.backgroundImage;
        delete json.overlay;
        delete json.overlayImage;
      }
      const state = {
        v: 2,
        widthMm: this.widthMm,
        heightMm: this.heightMm,
        thicknessMm: this.thicknessMm,
        material: this.material,
        bgColor: this.bgColor,
        stitchColor: this.stitchColor,
        canvas: json,
        ts: Date.now(),
      };
      localStorage.setItem(this.autosaveKey(), JSON.stringify(state));
      this.setSaveStatus('Сохранено локально');
    } catch (e) {
      console.warn('[TMD] autosave failed', e);
      this.setSaveStatus('⚠ Автосохранение недоступно (браузер заполнен)');
    }
  }

  loadAutostate() {
    try {
      const raw = localStorage.getItem(this.autosaveKey());
      if (!raw) return false;
      const state = JSON.parse(raw);
      if (!state || state.v !== 2 || !state.canvas) {
        // Старая схема: инвалидируем, чтобы не тянуть мусор (напр. backgroundImage удалённых шаблонов)
        if (state && state.v !== 2) {
          try { localStorage.removeItem(this.autosaveKey()); } catch (e) {}
        }
        return false;
      }

      // Guard: блокируем autosave до полного восстановления
      this.isRestoring = true;

      // Применить размеры ДО загрузки объектов
      if (state.widthMm && state.heightMm &&
          (state.widthMm !== this.widthMm || state.heightMm !== this.heightMm)) {
        this.widthMm = state.widthMm;
        this.heightMm = state.heightMm;
        this.displayPxPerMm = this.computeDisplayPxPerMm();
        this.displayWidth = Math.round(this.widthMm * this.displayPxPerMm);
        this.displayHeight = Math.round(this.heightMm * this.displayPxPerMm);
        this.printWidthPx = Math.round((this.widthMm / INCH_MM) * this.printDpi);
        this.printHeightPx = Math.round((this.heightMm / INCH_MM) * this.printDpi);
        this.printMultiplier = this.printWidthPx / this.displayWidth;
        this.canvas.setDimensions({ width: this.displayWidth, height: this.displayHeight });
        // UI sync
        this.root.querySelectorAll('[data-group="size"] .tmd-pill').forEach(b => {
          b.classList.toggle('active',
            Number(b.dataset.w) === this.widthMm && Number(b.dataset.h) === this.heightMm);
        });
        const sub = this.root.querySelector('.tmd-brand-sub');
        if (sub) sub.textContent = this.widthMm + '×' + this.heightMm + ' мм · ' +
          this.printDpi + ' DPI · печать ' + this.printWidthPx + '×' + this.printHeightPx + ' px';
        const titleEl = this.root.querySelector('.tmd-brand-title');
        if (titleEl) titleEl.textContent = 'Коврик ' + this.widthMm + '×' + this.heightMm + ' мм';
        const info = this.root.querySelector('#tmd-print-info');
        if (info) info.textContent = this.printWidthPx + '×' + this.printHeightPx + ' px';
      }

      // Применить опции
      if (state.thicknessMm) this.setThickness(state.thicknessMm);
      if (state.material) this.setMaterial(state.material);
      if (state.bgColor) this.setBgColor(state.bgColor);
      if (state.stitchColor) this.setStitchColor(state.stitchColor);

      // Восстановить canvas
      this.canvas.loadFromJSON(state.canvas).then(() => {
        // Никаких остатков старого template-бэкграунда
        this.canvas.backgroundImage = null;
        this.canvas.overlayImage = null;
        // safe-zone: пересоздать (она excludeFromExport, могла не войти в JSON)
        if (!this.canvas.getObjects().includes(this.safeZone)) {
          this.resetSafeZone();
        }
        this.canvas.backgroundColor = this.bgColor;
        this.canvas.requestRenderAll();
        this.isRestoring = false;
        // Сброс истории: единственная точка = восстановленное состояние
        this.history = [this.canvas.toJSON(['data'])];
        this.historyIndex = 0;
        this.refreshLayers();
        this.renderObjectPanel(null);
        this.updateDpiHint();
        this.updateEmptyState();
        this.setSaveStatus('Дизайн восстановлен');
      });
      console.log('[TMD] autostate restored', {
        size: [state.widthMm, state.heightMm],
        objects: (state.canvas.objects || []).length,
      });
      return true;
    } catch (e) {
      console.warn('[TMD] restore failed', e);
      return false;
    }
  }

  clearAutostate() {
    try { localStorage.removeItem(this.autosaveKey()); } catch (e) {}
  }

  setSaveStatus(text) {
    const el = this.root.querySelector('#tmd-save-status');
    if (el) el.textContent = text;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const root = document.getElementById('tmd-root');
  if (!root) return;
  try {
    window.__tmd = new MatDesigner(root);
  } catch (err) {
    console.error('[TMD] init failed', err);
    root.innerHTML = `<div class="tmd-error">Ошибка инициализации: ${err.message}</div>`;
  }
});
